import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Activity, AlertTriangle, ArrowRight, Clock3, Database, FileWarning, HeartPulse, RefreshCw, Users } from "lucide-react";
import api from "../../utils/api";

const TONES = {
  blue: "bg-blue-50 text-blue-700 border-blue-100",
  emerald: "bg-emerald-50 text-emerald-700 border-emerald-100",
  amber: "bg-amber-50 text-amber-700 border-amber-100",
  rose: "bg-rose-50 text-rose-700 border-rose-100",
  violet: "bg-violet-50 text-violet-700 border-violet-100",
  slate: "bg-slate-50 text-slate-700 border-slate-100",
};

const ICONS = [Users, Activity, Clock3, FileWarning, Database];

function display(value) {
  if (value === null || value === undefined || value === "") return "—";
  if (/(_at|date|start_date|end_date|last_received)$/i.test(String(value.key || ""))) {
    const date = new Date(value.value);
    if (!Number.isNaN(date.getTime())) return date.toLocaleString([], { dateStyle: "medium", timeStyle: "short" });
  }
  return String(value.value).replace(/_/g, " ");
}

function humanize(value) {
  return String(value || "").replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export default function RoleBasedDashboardPanel() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeSection, setActiveSection] = useState(0);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await api.get("/dashboard/role-view");
      setData(response.data);
      setActiveSection(0);
    } catch (requestError) {
      setError(requestError?.response?.data?.message || "Role dashboard could not be loaded.");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const currentSection = data?.sections?.[activeSection] || null;
  const columns = useMemo(() => {
    const first = currentSection?.rows?.[0];
    if (!first) return [];
    return Object.keys(first).filter((key) => key !== "id").slice(0, 6);
  }, [currentSection]);

  if (loading) return <div className="h-64 animate-pulse rounded-[28px] border border-slate-200 bg-white p-6"><div className="h-5 w-40 rounded bg-slate-200" /><div className="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-5">{Array.from({ length: 5 }).map((_, index) => <div key={index} className="h-24 rounded-2xl bg-slate-100" />)}</div></div>;
  if (error) return <div className="flex items-center justify-between gap-4 rounded-2xl border border-rose-100 bg-rose-50 p-4 text-rose-700"><div className="flex items-center gap-3"><AlertTriangle size={18} /><p className="text-sm font-bold">{error}</p></div><button type="button" onClick={load} className="rounded-xl bg-white px-3 py-2 text-xs font-black"><RefreshCw size={14} className="inline" /> Retry</button></div>;
  if (!data) return null;

  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm">
      <header className="relative overflow-hidden bg-slate-950 px-5 py-6 text-white sm:px-7">
        <div className="absolute -right-14 -top-20 h-52 w-52 rounded-full bg-customRed/20 blur-3xl" />
        <div className="relative flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div><p className="text-[10px] font-black uppercase tracking-[0.18em] text-rose-300">{humanize(data.persona)} dashboard</p><h2 className="mt-2 text-2xl font-black tracking-tight">{data.title}</h2><p className="mt-1 text-sm text-slate-300">{data.subtitle}</p></div>
          <div className="flex flex-wrap gap-2">{data.quick_links?.map((link) => <button key={link.to} type="button" onClick={() => navigate(link.to)} className="rounded-xl border border-white/15 bg-white/10 px-3 py-2 text-[10px] font-black uppercase tracking-wide text-white transition hover:bg-white hover:text-slate-900">{link.label} <ArrowRight size={12} className="ml-1 inline" /></button>)}</div>
        </div>
      </header>
      <div className="p-4 sm:p-6">
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">{data.metrics?.map((item, index) => { const MetricIcon = ICONS[index % ICONS.length]; return <div key={item.label} className={`rounded-2xl border p-4 ${TONES[item.tone] || TONES.slate}`}><div className="flex items-start justify-between gap-2"><div><p className="text-[9px] font-black uppercase tracking-[0.12em] opacity-70">{item.label}</p><p className="mt-2 text-2xl font-black capitalize">{item.value}</p></div><MetricIcon size={18} className="opacity-70" /></div><p className="mt-2 text-[10px] font-semibold opacity-70">{item.detail}</p></div>; })}</div>
        {data.sections?.length > 0 && <div className="mt-6"><div className="flex gap-2 overflow-x-auto pb-2">{data.sections.map((section, index) => <button key={section.key} type="button" onClick={() => setActiveSection(index)} className={`shrink-0 rounded-xl px-4 py-2.5 text-xs font-black transition ${index === activeSection ? "bg-customRed text-white shadow-md shadow-red-200" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{section.title}</button>)}</div><div className="mt-3 overflow-hidden rounded-2xl border border-slate-100"><div className="overflow-x-auto">{currentSection?.rows?.length ? <table className="min-w-[680px] w-full text-left"><thead className="bg-slate-50"><tr>{columns.map((column) => <th key={column} className="px-4 py-3 text-[9px] font-black uppercase tracking-wider text-slate-400">{humanize(column)}</th>)}</tr></thead><tbody className="divide-y divide-slate-100">{currentSection.rows.map((row, rowIndex) => <tr key={row.id || rowIndex} className="hover:bg-slate-50/70">{columns.map((column) => <td key={column} className="max-w-[220px] truncate px-4 py-3 text-xs font-semibold text-slate-700">{display({ key: column, value: row[column] })}</td>)}</tr>)}</tbody></table> : <div className="flex flex-col items-center py-10 text-center"><HeartPulse className="text-emerald-400" size={26} /><p className="mt-3 text-sm font-black text-slate-700">No items need attention</p><p className="mt-1 text-xs text-slate-400">This section is clear right now.</p></div>}</div></div></div>}
      </div>
    </section>
  );
}
